import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export const COMPACT_PORTRAIT_QUERY = '(max-width: 768px) and (orientation: portrait)';
const NavigationContext = createContext(null);

export function NavigationProvider({ children }) {
  const [actions, setActions] = useState({});
  const [preferences, setPreferences] = useState({});
  const register = useCallback((key, value) => {
    setActions(current => ({ ...current, [key]: value }));
    return () => setActions(current => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);
  const registerPage = useCallback(value => {
    setPreferences(value);
    return () => setPreferences({});
  }, []);

  return (
    <NavigationContext.Provider value={{ actions, preferences, register, registerPage }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  return useContext(NavigationContext);
}

// Keep callbacks current without re-registering on every page render.
export function useNavigationAction(key, action) {
  const context = useNavigation();
  const register = context?.register;
  const callback = useRef(action?.onClick);
  const content = useRef(action?.renderContent);
  // Keep the last handlers until the effect unregisters a removed action.
  if (action) {
    callback.current = action.onClick;
    content.current = action.renderContent;
  }
  const hasContent = Boolean(action?.renderContent);
  const enabled = Boolean(action);
  const { label, icon, parent, disabled = false } = action || {};
  useEffect(() => {
    if (!register || !enabled) return undefined;
    return register(key, {
      label, icon, parent, disabled, onClick: () => callback.current?.(),
      renderContent: hasContent ? (options) => content.current(options) : undefined,
    });
  }, [register, key, enabled, label, icon, parent, disabled, hasContent]);
}

export function usePageNavigation({
  primaryAction,
  preferredOrientation = 'any',
  preferredLayout = 'adaptive',
}) {
  const context = useNavigation();
  const registerPage = context?.registerPage;
  useNavigationAction('primary', primaryAction);
  useEffect(() => {
    if (!registerPage) return undefined;
    return registerPage({ preferredOrientation, preferredLayout });
  }, [registerPage, preferredOrientation, preferredLayout]);
}

export function useCompactPortrait() {
  const [compact, setCompact] = useState(() => window.matchMedia(COMPACT_PORTRAIT_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(COMPACT_PORTRAIT_QUERY);
    const update = () => setCompact(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return compact;
}
